import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { logoBiolab } from '../../helpers/imagen/Imagen';

// ─── Constantes visuales ─────────────────────────────────────────────────────
const AZUL       = [44, 95, 141];
const AZUL_LABEL = [235, 241, 248];
const L          = 1.5;   // margen izquierdo cm
const R          = 1.5;   // margen derecho cm
const BODY_TOP   = 2.8;   // espacio reservado para header cm
const FOOTER_Y   = 27.0;  // inicio del footer cm
const MAX_Y      = 26.3;  // máximo y de contenido antes de pedir nueva página

/**
 * Genera un PDF profesional del acta de préstamo
 * @param {Object} prestamo - Objeto con los datos completos del préstamo
 * @returns {jsPDF} - Documento PDF generado
 */
export const generarPDFPrestamo = (prestamo) => {

  console.log('Generando PDF para préstamo:', prestamo); // Debug: revisar datos del préstamo
  const doc        = new jsPDF({ unit: 'cm', orientation: 'p', format: 'A4' });
  const PW         = doc.internal.pageSize.getWidth();   // 21 cm
  const CONTENT_W  = PW - L - R;                         // 18 cm
  const pagesDrawn = new Set();
  const conf       = prestamo?.confirmacionEntrega || {};
  const recep      = prestamo?.recepcionOficina    || {};

  // ─── drawPageHeader ──────────────────────────────────────────────────────
  const drawPageHeader = (pageNum) => {
    if (pagesDrawn.has(pageNum)) return;
    pagesDrawn.add(pageNum);

    const logoW  = 3.5;
    const infoX  = L + logoW;
    const infoW  = CONTENT_W - logoW;
    const halfW  = infoW / 2;
    const rowH   = 0.65;
    const logoH  = rowH * 2;

    doc.setDrawColor(...AZUL);
    doc.setLineWidth(0.02);

    // Fila 1 — fondo azul con título institucional
    doc.setFillColor(...AZUL);
    doc.rect(L, 0.4, CONTENT_W, 0.55, 'F');
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('SISTEMA INTEGRADO DE GESTIÓN', PW / 2, 0.78, { align: 'center' });

    // Filas 2-3
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');

    // Celda logo (rowspan 2)
    doc.rect(L, 0.95, logoW, logoH);
    try {
      doc.addImage(logoBiolab, 'JPEG', L + 0.35, 1.0, 2.7, 1.1);
    } catch (_) { /* skip logo on error */ }

    // Celdas de info
    doc.rect(infoX,         0.95,        halfW, rowH);
    doc.rect(infoX + halfW, 0.95,        halfW, rowH);
    doc.rect(infoX,         0.95 + rowH, halfW, rowH);
    doc.rect(infoX + halfW, 0.95 + rowH, halfW, rowH);

    doc.setFontSize(7);
    const cy2 = 0.95 + rowH * 0.55;
    const cy3 = 0.95 + rowH + rowH * 0.55;
    doc.text('Proceso: Gestión Comercial',           infoX + halfW / 2,         cy2, { align: 'center' });
    doc.text('Código: PRE-GCO-FOR-001',              infoX + halfW + halfW / 2, cy2, { align: 'center' });
    doc.text('Formato: Acta de Préstamo de Equipos', infoX + halfW / 2,         cy3, { align: 'center' });
    doc.text('Versión: 01',                          infoX + halfW + halfW / 2, cy3, { align: 'center' });
  };

  // ─── drawPageFooter ──────────────────────────────────────────────────────
  const drawPageFooter = (pageNum) => {
    const colW   = CONTENT_W / 3;
    const labels = ['Elaboró: Director de Calidad', 'Revisó: Director de Laboratorio', 'Aprobó: Gerente'];
    doc.setDrawColor(...AZUL);
    doc.setLineWidth(0.02);
    doc.setFontSize(7);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    for (let i = 0; i < 3; i++) {
      doc.rect(L + i * colW, FOOTER_Y, colW, 0.6);
      doc.text(labels[i], L + i * colW + colW / 2, FOOTER_Y + 0.38, { align: 'center' });
    }
    doc.setTextColor(80, 80, 80);
    doc.text(`Página ${pageNum}`, PW / 2, FOOTER_Y + 0.85, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  };

  // ─── Callback compartido de autoTable ────────────────────────────────────
  const onDidDrawPage = (data) => {
    drawPageHeader(data.pageNumber);
    drawPageFooter(data.pageNumber);
  };

  // ─── Agregar página manualmente ──────────────────────────────────────────
  const addNewPage = () => {
    doc.addPage();
    const pg = doc.internal.getNumberOfPages();
    drawPageHeader(pg);
    drawPageFooter(pg);
    return BODY_TOP;
  };

  // ─── Helper formato de fecha ─────────────────────────────────────────────
  const fmt = (d) => d
    ? new Date(d).toLocaleString('es-CO', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    : 'N/A';

  // ─── Página 1 ─────────────────────────────────────────────────────────────
  drawPageHeader(1);
  drawPageFooter(1);
  let y = BODY_TOP;

  // ─── Título ──────────────────────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...AZUL);
  doc.text('ACTA DE PRÉSTAMO DE EQUIPOS', PW / 2, y, { align: 'center' });
  y += 0.7;
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');

  // ─── Información general ─────────────────────────────────────────────────
  autoTable(doc, {
    startY: y,
    theme: 'plain',
    styles: {
      fontSize: 8,
      cellPadding: { top: 0.1, bottom: 0.1, left: 0.2, right: 0.2 },
      lineColor: AZUL,
      lineWidth: 0.02
    },
    headStyles: { fillColor: AZUL, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center', fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 2.5, fontStyle: 'bold', fillColor: AZUL_LABEL },
      1: { cellWidth: 5.5 },
      2: { cellWidth: 2.5, fontStyle: 'bold', fillColor: AZUL_LABEL },
      3: { cellWidth: 7.5 }
    },
    head: [[{ content: 'INFORMACIÓN DEL PRÉSTAMO', colSpan: 4 }]],
    body: [
      ['Cliente:', prestamo.ot.cliente.nombre || 'N/A', 'Nit:', prestamo.ot.cliente.identificacion || 'N/A'],
      ['Acta No:',    prestamo.numeroActa || 'N/A',                           'Fecha:',  fmt(prestamo.fechaCreacion)],
      ['OT:',         prestamo.ot?.numeroOrden || prestamo.numeroOt || 'N/A', 'Estado:', (prestamo.estado || 'N/A').toUpperCase()],

    ],
    margin: { left: L, right: R, top: BODY_TOP, bottom: 2.5 },
    didDrawPage: onDidDrawPage
  });
  y = doc.lastAutoTable.finalY + 0.4;

  // ─── Tabla de equipos ─────────────────────────────────────────────────────
  const estadoEqMap = { devuelto: 'Devuelto', prestado: 'Prestado', devuelto_parcial: 'Dev. Parcial' };
  const equiposData = (prestamo.equipos || []).map((eq, i) => {
    const snap = eq.snapshotEquipo || {};
    return [
      i + 1,
      snap.inventario || eq.codigo || 'N/A',
      snap.item || eq.descripcion || 'N/A',
      [snap.marca, snap.modelo].filter(Boolean).join(' ') || '-',
      snap.serie || eq.serie || 'N/A',
      estadoEqMap[eq.estado] || eq.estado || 'N/A'
    ];
  });

  autoTable(doc, {
    startY: y,
    theme: 'grid',
    headStyles: { fillColor: AZUL, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center', fontSize: 8 },
    alternateRowStyles: { fillColor: [235, 241, 248] },
    styles: {
      fontSize: 8,
      cellPadding: { top: 0.1, bottom: 0.1, left: 0.15, right: 0.15 },
      lineColor: AZUL,
      lineWidth: 0.01
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 0.8 },
      1: { cellWidth: 2.8 },
      2: { cellWidth: 7.0 },
      3: { cellWidth: 3.0 },
      4: { cellWidth: 2.4 },
      5: { halign: 'center', cellWidth: 2.0 }
    },
    head: [
      [{ content: 'RELACIÓN DE EQUIPOS EN PRÉSTAMO', colSpan: 6 }],
      ['#', 'Inventario', 'Ítem / Descripción', 'Marca / Modelo', 'Serie', 'Estado']
    ],
    body: equiposData,
    margin: { left: L, right: R, top: BODY_TOP, bottom: 2.5 },
    didDrawPage: onDidDrawPage
  });
  y = doc.lastAutoTable.finalY + 0.4;

  // ─── Observaciones ────────────────────────────────────────────────────────
  if (prestamo.observaciones) {
    if (y > MAX_Y - 1.5) y = addNewPage();
    autoTable(doc, {
      startY: y,
      theme: 'plain',
      styles: {
        fontSize: 8,
        cellPadding: { top: 0.1, bottom: 0.1, left: 0.2, right: 0.2 },
        lineColor: AZUL,
        lineWidth: 0.02
      },
      headStyles: { fillColor: AZUL, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center', fontSize: 8 },
      head: [['OBSERVACIONES']],
      body: [[prestamo.observaciones]],
      margin: { left: L, right: R, top: BODY_TOP, bottom: 2.5 },
      didDrawPage: onDidDrawPage
    });
    y = doc.lastAutoTable.finalY + 0.4;
  }

  // ─── Confirmación de entrega ──────────────────────────────────────────────
  if (conf.confirmado) {
    if (y > MAX_Y - 2) y = addNewPage();

    const confBody = [
      ['Recibido por:', conf.nombreRecibe || 'N/A', 'Cargo:', conf.cargoRecibe || 'N/A'],
      ['Fecha:',        fmt(conf.fecha),             'A satisfacción:', conf.recibidoSatisfaccion ? 'Sí' : 'No']
    ];
    if (conf.observaciones) {
      confBody.push(['Observaciones:', { content: conf.observaciones, colSpan: 3 }]);
    }

    autoTable(doc, {
      startY: y,
      theme: 'plain',
      styles: {
        fontSize: 8,
        cellPadding: { top: 0.1, bottom: 0.1, left: 0.2, right: 0.2 },
        lineColor: AZUL,
        lineWidth: 0.02
      },
      headStyles: { fillColor: AZUL, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center', fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 3.0, fontStyle: 'bold', fillColor: AZUL_LABEL },
        1: { cellWidth: 6.0 },
        2: { cellWidth: 2.5, fontStyle: 'bold', fillColor: AZUL_LABEL },
        3: { cellWidth: 6.5 }
      },
      head: [[{ content: 'CONFIRMACIÓN DE ENTREGA', colSpan: 4 }]],
      body: confBody,
      margin: { left: L, right: R, top: BODY_TOP, bottom: 2.5 },
      didDrawPage: onDidDrawPage
    });
    y = doc.lastAutoTable.finalY + 0.4;
  }

  // ─── Recepción en oficina ─────────────────────────────────────────────────
  if (recep.fechaRecepcion) {
    if (y > MAX_Y - 2) y = addNewPage();

    const recepBody = [
      ['Recibido por:', recep.nombreUsuarioRecibe || 'N/A', 'Fecha:', fmt(recep.fechaRecepcion)],
      ['Estado:', { content: recep.recibidoCompleto ? 'Devolución Completa' : 'Devolución Parcial', colSpan: 3 }]
    ];
    if (recep.observaciones) {
      recepBody.push(['Observaciones:', { content: recep.observaciones, colSpan: 3 }]);
    }

    autoTable(doc, {
      startY: y,
      theme: 'plain',
      styles: {
        fontSize: 8,
        cellPadding: { top: 0.1, bottom: 0.1, left: 0.2, right: 0.2 },
        lineColor: AZUL,
        lineWidth: 0.02
      },
      headStyles: { fillColor: AZUL, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center', fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 3.5, fontStyle: 'bold', fillColor: AZUL_LABEL },
        1: { cellWidth: 5.5 },
        2: { cellWidth: 2.5, fontStyle: 'bold', fillColor: AZUL_LABEL },
        3: { cellWidth: 6.5 }
      },
      head: [[{ content: 'RECEPCIÓN EN OFICINA', colSpan: 4 }]],
      body: recepBody,
      margin: { left: L, right: R, top: BODY_TOP, bottom: 2.5 },
      didDrawPage: onDidDrawPage
    });
    y = doc.lastAutoTable.finalY + 0.4;
  }

  // ─── Sección de firmas ────────────────────────────────────────────────────
  const FIRMA_H = 2.8;
  if (y > MAX_Y - FIRMA_H - 1.5) y = addNewPage();

  y += 0.2;
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...AZUL);
  doc.text('FIRMAS', PW / 2, y, { align: 'center' });
  y += 0.5;
  doc.setTextColor(0, 0, 0);

  const firmaW  = (CONTENT_W - 1.0) / 2;
  const firmaX1 = L;
  const firmaX2 = L + firmaW + 1.0;

  doc.setDrawColor(...AZUL);
  doc.setLineWidth(0.02);

  // Cajas de firma
  doc.rect(firmaX1, y, firmaW, FIRMA_H);
  doc.rect(firmaX2, y, firmaW, FIRMA_H);

  // Headers de caja (fondo azul)
  doc.setFillColor(...AZUL);
  doc.rect(firmaX1, y, firmaW, 0.5, 'F');
  doc.rect(firmaX2, y, firmaW, 0.5, 'F');
  doc.setFontSize(8);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('ENTREGA (Técnico)', firmaX1 + firmaW / 2, y + 0.33, { align: 'center' });
  doc.text('RECIBE (Cliente)',  firmaX2 + firmaW / 2, y + 0.33, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  // Imagen de firma del receptor (ya viene como data URI completo)
  if (conf.firma) {
    try {
      const imgH = FIRMA_H - 1.0;
      const imgW = firmaW * 0.8;
      doc.addImage(conf.firma, 'PNG', firmaX2 + (firmaW - imgW) / 2, y + 0.6, imgW, imgH);
    } catch (_) { /* deja caja vacía si la imagen falla */ }
  }

  // Nombres y labels bajo las cajas
  const nombreY = y + FIRMA_H + 0.3;
  doc.setFont(undefined, 'normal');
  doc.setFontSize(8);

  const tecNombre = conf.nombreTecnico || `${prestamo.usuarioCreacion?.nombre || ''} ${prestamo.usuarioCreacion?.apellido || ''}`.trim() || '';
  if (tecNombre) doc.text(tecNombre, firmaX1 + firmaW / 2, nombreY, { align: 'center' });

  if (conf.nombreRecibe) {
    const recLabel = conf.cargoRecibe
      ? `${conf.nombreRecibe} — ${conf.cargoRecibe}`
      : conf.nombreRecibe;
    doc.text(recLabel, firmaX2 + firmaW / 2, nombreY, { align: 'center' });
  }

  doc.setFontSize(7);
  doc.setTextColor(80, 80, 80);
  doc.text('Nombre y Firma del Creador',     firmaX1 + firmaW / 2, nombreY + 0.32, { align: 'center' });
  doc.text('Nombre y Firma de quien Recibe', firmaX2 + firmaW / 2, nombreY + 0.32, { align: 'center' });

  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');

  return doc;
};

/**
 * Descarga el PDF del préstamo
 * @param {Object} prestamo - Objeto con los datos del préstamo
 * @param {string} nombreArchivo - Nombre opcional del archivo (sin extensión)
 */
export const descargarPDFPrestamo = (prestamo, nombreArchivo = null) => {
  try {
    const doc = generarPDFPrestamo(prestamo);
    const filename = nombreArchivo || `Prestamo_${prestamo.numeroActa || 'N/A'}`;
    doc.save(`${filename}.pdf`);
    return { ok: true, message: 'PDF generado correctamente' };
  } catch (error) {
    console.error('Error al generar PDF:', error);
    return { ok: false, message: 'Error al generar el PDF', error };
  }
};

/**
 * Abre el PDF en una nueva pestaña (vista previa)
 * @param {Object} prestamo - Objeto con los datos del préstamo
 */
export const previsualizarPDFPrestamo = (prestamo) => {
  try {
    const doc = generarPDFPrestamo(prestamo);
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
    return { ok: true, message: 'PDF abierto en nueva pestaña' };
  } catch (error) {
    console.error('Error al previsualizar PDF:', error);
    return { ok: false, message: 'Error al previsualizar el PDF', error };
  }
};

/**
 * Obtiene el PDF como Blob (útil para enviar por email, etc.)
 * @param {Object} prestamo - Objeto con los datos del préstamo
 * @returns {Blob} - PDF como blob
 */
export const obtenerBlobPDFPrestamo = (prestamo) => {
  try {
    const doc = generarPDFPrestamo(prestamo);
    return doc.output('blob');
  } catch (error) {
    console.error('Error al obtener blob del PDF:', error);
    return null;
  }
};

/**
 * Obtiene el PDF como base64 (útil para almacenar o transmitir)
 * @param {Object} prestamo - Objeto con los datos del préstamo
 * @returns {string} - PDF en formato base64
 */
export const obtenerBase64PDFPrestamo = (prestamo) => {
  try {
    const doc = generarPDFPrestamo(prestamo);
    return doc.output('datauristring');
  } catch (error) {
    console.error('Error al obtener base64 del PDF:', error);
    return null;
  }
};

export default {
  generarPDFPrestamo,
  descargarPDFPrestamo,
  previsualizarPDFPrestamo,
  obtenerBlobPDFPrestamo,
  obtenerBase64PDFPrestamo
};